import {CardSettingsStoreInterface} from '@/pages/business-account/CardsManagement'
import {injectable} from 'inversify'
import {makeAutoObservable} from 'mobx'
import {LoadingState} from '@/types'
import {fetchCardCVV, fetchCardPIN, fetchChangeCardStatus} from '@/stores'
import {openErrorNotification} from '@/utils'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'

@injectable()
export class CardSettingsStore implements CardSettingsStoreInterface {
    constructor() {
        makeAutoObservable(this)
    }

    isBlockModalVisible = false
    isFreezeModalVisible = false
    isGetCVVModalVisible = false
    isGetPINModalVisible = false
    pinData = null
    cvvData = null
    getCVVLoadingState = LoadingState.IDLE
    getPINLoadingState = LoadingState.IDLE
    changeCardStatusLoadingState = LoadingState.IDLE
    token = ''

    openBlockCardModal = () => {
        this.isBlockModalVisible = true
    }

    openFreezeCardModal = () => {
        this.isFreezeModalVisible = true
    }

    closeBlockCardModal = () => {
        this.isBlockModalVisible = false
    }

    closeFreezeCardModal = () => {
        this.isFreezeModalVisible = false
    }

    openGetCVVModal = () => {
        this.isGetCVVModalVisible = true
    }

    closeGetCVVModal = () => {
        this.isGetCVVModalVisible = false
        this.cvvData = null
    }

    openGetPINModal = () => {
        this.isGetPINModalVisible = true
    }

    closeGetPINModal = () => {
        this.isGetPINModalVisible = false
        this.pinData = null
    }

    setCardToken = (token: string) => {
        this.token = token
    }

    onGetCVVClick = async () => {
        if (this.getCVVLoadingState === LoadingState.LOADING) {
            return
        }

        try {
            this.getCVVLoadingState = LoadingState.LOADING

            const {result, error} = await fetchCardCVV(this.token)

            if (error) {
                this.getCVVLoadingState = LoadingState.FAILED
                throw new Error(error.message)
            }

            if (result) {
                this.getCVVLoadingState = LoadingState.DONE
                this.cvvData = result
            }
        } catch (e) {
            captureSentryError({
                name: 'Card GetCVV Error',
                description: getErrorMessage(e, 'Failed to get card CVV'),
                fingerprint: ['card-get-cvv'],
                error: e
            })
            openErrorNotification(e)
            this.getCVVLoadingState = LoadingState.FAILED
        }
    }

    onGetPINClick = async () => {
        if (this.getPINLoadingState === LoadingState.LOADING) {
            return
        }

        try {
            this.getPINLoadingState = LoadingState.LOADING

            const {result, error} = await fetchCardPIN(this.token)

            if (error) {
                this.getPINLoadingState = LoadingState.FAILED
                throw new Error(error.message)
            }

            if (result) {
                this.getPINLoadingState = LoadingState.DONE
                this.pinData = result
            }
        } catch (e) {
            captureSentryError({
                name: 'Card GetPIN Error',
                description: getErrorMessage(e, 'Failed to get card PIN'),
                fingerprint: ['card-get-pin'],
                error: e
            })
            this.getPINLoadingState = LoadingState.FAILED
            openErrorNotification(e)
        }
    }

    onChangeCardStatus = async values => {
        if (this.changeCardStatusLoadingState === LoadingState.LOADING) {
            return
        }

        try {
            this.changeCardStatusLoadingState = LoadingState.LOADING

            const {result, error} = await fetchChangeCardStatus(this.token, values.status)

            if (error) {
                this.changeCardStatusLoadingState = LoadingState.FAILED
                throw new Error(error.message)
            }

            if (result) {
                this.changeCardStatusLoadingState = LoadingState.DONE
            }
        } catch (e) {
            captureSentryError({
                name: 'Card ChangeStatus Error',
                description: getErrorMessage(e, 'Failed to change card status'),
                fingerprint: ['card-change-status'],
                error: e
            })
            this.changeCardStatusLoadingState = LoadingState.FAILED
            openErrorNotification(e)
        }
    }
}
